/**
 * Sliding idle-session bookkeeping.
 *
 * Firebase Auth keeps a session alive indefinitely — the SDK refreshes the ID
 * token every hour without consulting the user — so the 2-hour inactivity
 * limit is enforced client-side. The timestamp of the last user interaction
 * is persisted in localStorage, which is shared across tabs, so activity in
 * any tab slides the window for all of them. Both the router guard and the
 * useIdleSession watcher sign the user out once it goes stale.
 */

export const IDLE_LIMIT_MS = 2 * 60 * 60 * 1000

const LAST_ACTIVITY_KEY = 'aimsis.auth.lastActivityAt'

export function readLastActivity(storage: Storage = localStorage): number | null {
  const raw = storage.getItem(LAST_ACTIVITY_KEY)
  if (raw === null) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export function writeLastActivity(now: number = Date.now(), storage: Storage = localStorage): void {
  storage.setItem(LAST_ACTIVITY_KEY, String(now))
}

export function clearLastActivity(storage: Storage = localStorage): void {
  storage.removeItem(LAST_ACTIVITY_KEY)
}

/**
 * A missing timestamp is not expired: null means "no activity ever recorded"
 * (first sign-in after this shipped, or cleared storage), which the watcher
 * re-seeds to now rather than bouncing the session. A future timestamp is
 * not expired either — clock skew must not kick a live session out.
 */
export function isIdleExpired(
  lastActivity: number | null,
  now: number = Date.now(),
  limitMs: number = IDLE_LIMIT_MS,
): boolean {
  return lastActivity !== null && now - lastActivity >= limitMs
}
